import React, { useState } from "react";
import {
    Page, Card, Box, Text, BlockStack, InlineStack, Icon, Button
} from "@shopify/polaris";
import {
    ChevronDownIcon, ChevronUpIcon,
    ConfettiIcon, ClockIcon, CalendarIcon, DesktopIcon,
    CashDollarIcon, LockIcon, CalendarTimeIcon, CreditCardIcon, WrenchIcon,
    PersonAddIcon, ChartVerticalIcon, QuestionCircleIcon,
} from "@shopify/polaris-icons";
import { openSupport } from "../../support";
import { IconDisc } from "../ui/CardSurface";

const FAQ_CATEGORIES = [
    {
        category: "Getting Started",
        icon: ConfettiIcon,
        tone: "magic",
        faqs: [
            {
                question: "What does ZonStaff do?",
                answer: "ZonStaff is a staff time-clock, scheduling, and payroll app for your business. Your team clocks in and out from a shared web Clock Portal (a kiosk on a tablet or computer) using a personal 4-digit PIN. ZonStaff records their working hours, breaks, and shifts and turns them into timesheets, reports, and payroll — no paper timesheets needed."
            },
            {
                question: "How do I add my staff?",
                answer: "Go to Staff → Add Staff, or Add Non-Shopify Staff for team members without a linked store account. You can also Bulk Import a whole team from a file. Each staff member gets a 4-digit PIN that they use to clock in and out."
            },
            {
                question: "How do my staff clock in and out?",
                answer: "Through the Clock Portal — a web kiosk you open on a shared tablet or computer at your location. Each staff member taps their tile, enters their 4-digit PIN, and taps Clock In or Clock Out."
            },
            {
                question: "Do I have to create schedules first?",
                answer: "No. Staff can clock in and out without any schedule at all. Schedules are optional — they add shift windows (start/end times), a 'no shift today' guard, and richer reporting. Add them from the Schedule page whenever you're ready."
            }
        ]
    },
    {
        category: "Clocking In & Out",
        icon: ClockIcon,
        tone: "info",
        faqs: [
            {
                question: "What is the PIN and where do staff get it?",
                answer: "Every staff member has a private 4-digit PIN, set when you add or edit them under Staff. They type it on the Clock Portal to identify themselves. Entering the PIN once unlocks that staff member's actions for the session."
            },
            {
                question: "A staff member forgot to clock out — what happens?",
                answer: "Nothing breaks, and no fake clock-out is ever created. The next time they clock in, if the old shift is recent they'll see a 'A shift wasn't clocked out' notice with a Start New Session button — tapping it clocks them in fresh for today and leaves yesterday's shift open for you to fix. If it's an older forgotten shift, they simply clock in as normal. You then correct the missed shift from Staff → the staff member → Add Clock Out, entering the real end time."
            },
            {
                question: "Do overnight shifts work?",
                answer: "Yes. Shifts that cross midnight (e.g. 9 PM–5 AM) are fully supported. The session spans midnight and the timers keep counting correctly; the staff member clocks out the next morning as normal."
            },
            {
                question: "Can staff take breaks?",
                answer: "Yes. While clocked in, staff tap Start Break and later End Break. Break time is tracked separately, and — if you enable break deduction in Settings — it is subtracted from their paid hours."
            },
            {
                question: "The clock timer shows 00:00 or looks wrong",
                answer: "Timers are anchored to each punch's own timezone, so they show real elapsed time. If a device's clock or timezone is far off, correct the device time first. If you recently updated the app, refresh the Clock Portal page so it loads the latest version."
            }
        ]
    },
    {
        category: "Scheduling",
        icon: CalendarIcon,
        tone: "info",
        faqs: [
            {
                question: "How do I create a shift?",
                answer: "Go to Schedule, pick the staff member and day, set the start and end time (plus an optional break and notes), and save. You can repeat a shift across the rest of the week in one step."
            },
            {
                question: "What if a staff member has no shift today?",
                answer: "If they have schedules but none for today, they'll be told 'No shift scheduled for today.' If they have no schedules at all, they can clock in freely — ZonStaff treats them as an unscheduled worker."
            },
            {
                question: "What if a staff member has two shifts in one day?",
                answer: "They'll see a shift picker and choose which shift they're clocking into, so the hours are recorded against the correct shift."
            },
            {
                question: "Can staff clock in early or late?",
                answer: "You control this in Settings with an early-clock-in grace window (for example, up to 30 minutes before the shift). Outside the allowed window they'll see a message such as 'Your shift starts at 09:00.'"
            }
        ]
    },
    {
        category: "Clock Portal (Kiosk)",
        icon: DesktopIcon,
        tone: "info",
        faqs: [
            {
                question: "What is the Clock Portal?",
                answer: "It's the staff-facing web page your team uses to clock in and out — designed for a shared tablet or computer at your location (a kiosk). Staff tap their tile, enter their PIN, and can clock in/out, start/end breaks, view today's history, and see their day/week/month totals."
            },
            {
                question: "Can staff use it in their own language?",
                answer: "Yes. The Clock Portal supports 9 languages: English, German, Spanish, French, Italian, Dutch, Japanese, Simplified Chinese, and Portuguese. Staff switch languages with the language selector, and the choice is remembered on that device."
            },
            {
                question: "What is the Manager View?",
                answer: "Managers enter a manager PIN to reach manager-only actions — such as clocking a staff member in or out on their behalf — without exposing those controls to regular staff."
            },
            {
                question: "Can I require staff to be at my location (GPS)?",
                answer: "Yes. Enable GPS in Settings and the Clock Portal will require a location before allowing a punch. Staff who block location access or are out of range are prevented from clocking in."
            }
        ]
    },
    {
        category: "Payroll, Reports & Timesheets",
        icon: CashDollarIcon,
        tone: "success",
        faqs: [
            {
                question: "How are working hours calculated?",
                answer: "ZonStaff pairs each clock-in with its clock-out to measure the session length, sums a staff member's sessions for the day, week, and month, and shows the paid total. When break deduction is enabled, break time is removed from the paid total."
            },
            {
                question: "Where do I see hours and totals?",
                answer: "Use the Reports page for overviews, the Payroll page to run payroll, and each staff member's detail page under Staff for their individual hours, breaks, and earnings. Staff also see their own day/week/month totals on the kiosk."
            },
            {
                question: "Do the kiosk totals match the payroll report?",
                answer: "Yes. The kiosk and the admin reports use the same calculation, so what your staff see on the clock matches what you pay in payroll."
            },
            {
                question: "My staff work in a different timezone — are the hours correct?",
                answer: "Yes. Each punch is stored with the timezone it was made in, so totals reflect real elapsed time and aren't thrown off by the server's timezone or an offset."
            }
        ]
    },
    {
        category: "Settings & Security",
        icon: LockIcon,
        tone: "warning",
        faqs: [
            {
                question: "Can I require a PIN for every clock action?",
                answer: "Yes. With the PIN requirement on, every clock in, clock out, and break must be authorized by a valid PIN, so staff can't clock one another in by accident or on purpose."
            },
            {
                question: "Should I turn on break deduction?",
                answer: "Turn it on if breaks are unpaid at your business — break time is then subtracted from paid hours. Leave it off if breaks are paid. Staff totals and the payroll report both follow this setting, so they always agree."
            },
            {
                question: "What data does the app store?",
                answer: "ZonStaff stores your staff records (name, PIN, position, pay rate, locations) and their clock in/out, break, schedule, time-off, and task activity — the data needed to power timesheets, reports, and payroll."
            }
        ]
    },
    {
        category: "Time Off & Task Lists",
        icon: CalendarTimeIcon,
        tone: "success",
        faqs: [
            {
                question: "How does time off work?",
                answer: "Staff (or you) submit time-off requests, and managers approve or decline them on the Time Off page. You can also define time-off policies (such as paid or unpaid leave) there."
            },
            {
                question: "What are task lists?",
                answer: "Task lists let you assign daily, weekly, or monthly tasks to staff, which they complete from their portal — ideal for opening and closing checklists. Create and manage them on the Tasklist page."
            }
        ]
    },
    {
        category: "Plans & Billing",
        icon: CreditCardIcon,
        tone: "magic",
        faqs: [
            {
                question: "How do plans work?",
                answer: "Choose or change your plan on the Plans page. Your plan determines which features and limits are available to your account; changes take effect as soon as the new plan is active."
            },
            {
                question: "Can I change my plan later?",
                answer: "Yes — go to Plans at any time and select a different plan."
            }
        ]
    },
    {
        category: "Troubleshooting",
        icon: WrenchIcon,
        tone: "critical",
        faqs: [
            {
                question: "'View today's history' shows 'No History Available'",
                answer: "This usually means the PIN wasn't accepted, or there genuinely are no punches yet today. Confirm the staff member entered the correct PIN; if it persists right after clocking in, refresh the Clock Portal so it reloads today's records."
            },
            {
                question: "A staff member can't clock in",
                answer: "The most common reasons are: they're outside their shift window ('Your shift starts at…' / 'Your shift ended at…'), they have schedules but none for today ('No shift scheduled for today'), or an earlier shift needs closing. The on-screen message tells you which — and for a forgotten shift they can use Start New Session, or you can fix it from the staff member's detail page."
            },
            {
                question: "Text still shows in a different language, or the page looks outdated",
                answer: "Refresh the Clock Portal page so it loads the newest version. Language choices apply to the whole portal, including the tiles and the screens they open."
            }
        ]
    }
];

const STEPS = [
    { step: "1", label: "Add Staff & PINs", icon: PersonAddIcon, tone: "info" },
    { step: "2", label: "Staff Clock In", icon: ClockIcon, tone: "info" },
    { step: "3", label: "Hours Tracked", icon: ChartVerticalIcon, tone: "success" },
    { step: "4", label: "Run Payroll", icon: CashDollarIcon, tone: "success" },
];

export default function Faq() {
    const [openItem, setOpenItem] = useState(null);
    const toggle = (key) => setOpenItem(openItem === key ? null : key);

    // openSupport() opens mail. It used to open Crisp live chat; the chat was
    // removed, and the guard that used to sit here - `if (window.$crisp)` - was
    // always true anyway, because the bootstrap set window.$crisp = [] before
    // the SDK is requested, so the fallback could never fire and this button did
    // nothing at all when Crisp was blocked. See src/support.js.
    const contactSupport = openSupport;

    return (
        <Page
            title="Help & FAQ"
            subtitle="Everything you need to know about staff clock in/out, scheduling, and payroll with ZonStaff"
        >
            <BlockStack gap="600">

                {/* How it works banner */}
                <Card>
                    <Box padding="600">
                        <BlockStack gap="500">
                            <BlockStack gap="200">
                                <InlineStack gap="300" blockAlign="center">
                                    <IconDisc icon={ClockIcon} tone="info" size={36} />
                                    <Text variant="headingLg" as="h2" fontWeight="bold">How ZonStaff Works</Text>
                                </InlineStack>
                                <Text variant="bodyMd" tone="subdued" as="p">
                                    Your staff clock in and out from a shared kiosk using a personal PIN. ZonStaff tracks their hours, breaks, and shifts automatically — ready for reports and payroll.
                                </Text>
                            </BlockStack>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
                                {STEPS.map((s) => (
                                    <Box
                                        key={s.step}
                                        background="bg-surface-secondary"
                                        borderRadius="200"
                                        padding="400"
                                        borderWidth="025"
                                        borderColor="border"
                                    >
                                        <BlockStack gap="150" inlineAlign="center">
                                            <IconDisc icon={s.icon} tone={s.tone} size={36} />
                                            <Text variant="bodyXs" tone="subdued" as="p" alignment="center">
                                                {`Step ${s.step}`}
                                            </Text>
                                            <Text variant="bodySm" fontWeight="semibold" as="p" alignment="center">{s.label}</Text>
                                        </BlockStack>
                                    </Box>
                                ))}
                            </div>
                        </BlockStack>
                    </Box>
                </Card>

                {/* FAQ Categories */}
                {FAQ_CATEGORIES.map((cat) => (
                    <Card key={cat.category} padding="0">
                        {/* Category header */}
                        <Box
                            paddingInline="500"
                            paddingBlock="400"
                            borderBlockEndWidth="025"
                            borderColor="border"
                            background="bg-surface-secondary"
                        >
                            <InlineStack gap="300" blockAlign="center">
                                <IconDisc icon={cat.icon} tone={cat.tone} size={32} />
                                <Text variant="headingMd" as="h3" fontWeight="bold">{cat.category}</Text>
                                <div style={{ marginLeft: 'auto' }}>
                                    <Text variant="bodySm" tone="subdued" as="span">
                                        {`${cat.faqs.length} questions`}
                                    </Text>
                                </div>
                            </InlineStack>
                        </Box>

                        {/* FAQ Items */}
                        {cat.faqs.map((faq, idx) => {
                            const key = `${cat.category}-${idx}`;
                            const isOpen = openItem === key;
                            const isLast = idx === cat.faqs.length - 1;
                            return (
                                <div key={key}>
                                    <Box
                                        paddingInline="500"
                                        paddingBlock="400"
                                        borderBlockEndWidth={isLast ? "0" : "025"}
                                        borderColor="border-secondary"
                                    >
                                        <BlockStack gap="300">
                                            <button
                                                type="button"
                                                onClick={() => toggle(key)}
                                                aria-expanded={isOpen}
                                                style={{
                                                    all: 'unset',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    width: '100%',
                                                    gap: '12px',
                                                }}
                                            >
                                                <Text variant="bodyMd" fontWeight="semibold" as="span">
                                                    {faq.question}
                                                </Text>
                                                <span style={{ flexShrink: 0, color: 'var(--p-color-icon-secondary)' }}>
                                                    <Icon source={isOpen ? ChevronUpIcon : ChevronDownIcon} tone="subdued" />
                                                </span>
                                            </button>

                                            {isOpen && (
                                                <Box
                                                    background="bg-surface-secondary"
                                                    borderRadius="200"
                                                    padding="400"
                                                    borderWidth="025"
                                                    borderColor="border"
                                                >
                                                    <Text variant="bodyMd" tone="subdued" as="p">
                                                        {faq.answer}
                                                    </Text>
                                                </Box>
                                            )}
                                        </BlockStack>
                                    </Box>
                                </div>
                            );
                        })}
                    </Card>
                ))}

                {/* Support CTA */}
                <Card>
                    <Box padding="600">
                        <BlockStack gap="300" inlineAlign="center">
                            <IconDisc icon={QuestionCircleIcon} tone="info" size={40} />
                            <Text variant="headingMd" as="h3" alignment="center">Still have questions?</Text>
                            <Text variant="bodyMd" tone="subdued" as="p" alignment="center">
                                Our support team is here to help you get the most out of ZonStaff.
                            </Text>
                            <Box paddingBlockStart="200">
                                <Button variant="primary" onClick={contactSupport} size="large">
                                    Contact Support
                                </Button>
                            </Box>
                        </BlockStack>
                    </Box>
                </Card>

            </BlockStack>
        </Page>
    );
}
